import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchLeaveTypesStart, fetchLeaveTypesSuccess, fetchLeaveTypesFailure } from '../../../redux/slices/leaveTypesSlice';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getLeaveTypes, createLeaveType } from '../../../Action/api';

const LeaveTypes = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  
  const { data: leaveTypesData, loading, error } = useSelector((state) => state.leaveTypes);
  
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [filteredLeaveTypes, setFilteredLeaveTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [countryCode, setCountryCode] = useState('IN');
  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [formData, setFormData] = useState({
    leaveCode: '',
    leaveName: '',
    leaveCount: '',
    leaveGender: 'Male/Female',
    approvalType: 'Auto',
    leaveRules: '',
    countryCode: 'IN'
  });

  useEffect(() => {
    fetchLeaveTypes();
  }, [countryCode]);

  useEffect(() => {
    // Update local state when Redux data changes for current country
    if (leaveTypesData[countryCode]) {
      setLeaveTypes(leaveTypesData[countryCode]);
      setFilteredLeaveTypes(leaveTypesData[countryCode]);
    }
  }, [leaveTypesData, countryCode]);

  const fetchLeaveTypes = async () => {
    // Check if data exists in Redux for this country
    if (leaveTypesData[countryCode] && leaveTypesData[countryCode].length > 0) {
      setLeaveTypes(leaveTypesData[countryCode]);
      setFilteredLeaveTypes(leaveTypesData[countryCode]);
      return;
    }

    try {
      dispatch(fetchLeaveTypesStart());
      const data = await getLeaveTypes(countryCode);
      console.log('Fetched leave types:', data.length, 'records');
      dispatch(fetchLeaveTypesSuccess({ countryCode, data }));
    } catch (err) {
      dispatch(fetchLeaveTypesFailure('Failed to fetch leave types. Please try again.'));
      console.error('Error fetching leave types:', err);
    }
  };

  useEffect(() => {
    let filtered = leaveTypes.filter((leave) =>
      leave.leaveName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leave.leaveCode.toLowerCase().includes(searchTerm.toLowerCase())
    );

    console.log('After filter:', filtered.length, 'records');

    // Apply sorting - create a copy to avoid mutation
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];
        
        // Handle null/undefined values
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;
        
        if (sortColumn === 'leaveName' || sortColumn === 'leaveGender' || sortColumn === 'approvalType') {
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      console.log('After sort:', filtered.length, 'records');
    }

    setFilteredLeaveTypes(filtered);
    setPage(0);
  }, [searchTerm, leaveTypes, sortColumn, sortDirection]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCountryChange = (event) => {
    setCountryCode(event.target.value);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    setFormData({
      leaveCode: '',
      leaveName: '',
      leaveCount: '',
      leaveGender: 'Male/Female',
      approvalType: 'Auto',
      leaveRules: '',
      countryCode: countryCode
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const handleFormChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.leaveCode || !formData.leaveName || !formData.leaveCount) {
        setSubmitError('Please fill in all required fields');
        return;
      }

      setSubmitting(true);
      setSubmitError(null);

      // Convert leaveCount to number
      const submitData = {
        ...formData,
        leaveCount: parseInt(formData.leaveCount, 10)
      };

      await createLeaveType(submitData);
      
      setSubmitSuccess(true);
      
      // Refresh the leave types list
      await fetchLeaveTypes();
      
      // Close dialog after short delay
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);
      
    } catch (err) {
      setSubmitError(err.message || 'Failed to create leave type. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedLeaveTypes = filteredLeaveTypes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2 }, 
      maxWidth: { xs: '100vw', md: 'calc(100vw - 280px)' }, 
      mx: 'auto',
      width: '100%'
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 2,
        gap: { xs: 1, sm: 2 }
      }}>
        <Typography 
          variant={isMobile ? 'h6' : 'h5'} 
          component="h1" 
          sx={{ fontWeight: 600 }}
        >
          Leave Types Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
            <InputLabel>Country</InputLabel>
            <Select
              value={countryCode}
              label="Country"
              onChange={handleCountryChange}
            >
              <MenuItem value="IN">India</MenuItem>
              <MenuItem value="US">USA</MenuItem>
              <MenuItem value="GB">UK</MenuItem>
              <MenuItem value="CA">Canada</MenuItem>
              <MenuItem value="AU">Australia</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            size="small"
            onClick={fetchLeaveTypes}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {!isMobile && 'Reload'}
          </Button>
          <Button 
            variant="contained" 
            startIcon={!isMobile && <AddIcon />}
            size="small"
            fullWidth={isMobile}
            sx={{ textTransform: 'none' }}
            onClick={handleOpenDialog}
          >
            Add Leave Type
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by leave name or code..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <>
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 2,
          overflowX: 'auto',
          width: '100%',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '4px',
          }
        }}
      >
        <Table size="small" sx={{ minWidth: { xs: 600, sm: 700 } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>S.No</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Leave Code</TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#e0e0e0' }
                }}
                onClick={() => handleSort('leaveName')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Leave Name
                  {sortColumn === 'leaveName' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Leave Count</TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#e0e0e0' }
                }}
                onClick={() => handleSort('leaveGender')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Gender
                  {sortColumn === 'leaveGender' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#e0e0e0' }
                }}
                onClick={() => handleSort('approvalType')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Approval Type
                  {sortColumn === 'approvalType' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Created At</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLeaveTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No leave types found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedLeaveTypes.map((leave, index) => (
                <TableRow 
                  key={`${leave.leaveCode}-${page * rowsPerPage + index}`}
                  sx={{ 
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                  }}
                >
                  <TableCell sx={{ fontSize: '0.813rem' }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>
                    <Chip label={leave.leaveCode} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.813rem', fontWeight: 500 }}>{leave.leaveName}</TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>{leave.leaveCount} days</TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>
                    <Chip 
                      label={leave.leaveGender} 
                      size="small"
                      color={leave.leaveGender === 'Male/Female' ? 'primary' : leave.leaveGender === 'Female' ? 'secondary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>
                    <Chip 
                      label={leave.approvalType} 
                      color={leave.approvalType === 'Auto' ? 'success' : leave.approvalType === 'Manual' ? 'warning' : 'info'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>
                    {leave.createdAt ? new Date(leave.createdAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={Paper}
        sx={{ 
          mt: 1, 
          boxShadow: 1,
          '.MuiTablePagination-toolbar': {
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            justifyContent: 'center'
          },
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        count={filteredLeaveTypes.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={isMobile ? 'Rows:' : 'Rows per page:'}
      />
        </>
      )}

      {/* Add Leave Type Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">
            Add New Leave Type
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}
          
          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Leave type created successfully!
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Leave Code"
                value={formData.leaveCode}
                onChange={handleFormChange('leaveCode')}
                placeholder="e.g., UAL001"
                disabled={submitting}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Leave Name"
                value={formData.leaveName}
                onChange={handleFormChange('leaveName')}
                placeholder="e.g., Annual Leave"
                disabled={submitting}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Leave Count (Days)"
                value={formData.leaveCount}
                onChange={handleFormChange('leaveCount')}
                placeholder="e.g., 15"
                disabled={submitting}
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={submitting}>
                <InputLabel>Country Code</InputLabel>
                <Select
                  value={formData.countryCode}
                  label="Country Code"
                  onChange={handleFormChange('countryCode')}
                >
                  <MenuItem value="IN">India (IN)</MenuItem>
                  <MenuItem value="US">USA (US)</MenuItem>
                  <MenuItem value="GB">UK (GB)</MenuItem>
                  <MenuItem value="CA">Canada (CA)</MenuItem>
                  <MenuItem value="AU">Australia (AU)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={submitting}>
                <InputLabel>Approval Type</InputLabel>
                <Select
                  value={formData.approvalType}
                  label="Approval Type"
                  onChange={handleFormChange('approvalType')}
                >
                  <MenuItem value="Auto">Auto</MenuItem>
                  <MenuItem value="Manual">Manual</MenuItem>
                  <MenuItem value="Manual / Auto">Manual / Auto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={submitting}>
                <InputLabel>Leave Gender</InputLabel>
                <Select
                  value={formData.leaveGender}
                  label="Leave Gender"
                  onChange={handleFormChange('leaveGender')}
                >
                  <MenuItem value="Male/Female">Male/Female</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Leave Rules"
                value={formData.leaveRules}
                onChange={handleFormChange('leaveRules')}
                placeholder="e.g., Auto Approval if No Task"
                disabled={submitting}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseDialog} 
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            {submitting ? 'Creating...' : 'Create Leave Type'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveTypes;
